import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from '@rails/activestorage'
import Dropzone from 'dropzone'
import 'dropzone/dist/dropzone.css'
import { post } from '@rails/request.js'

import {
  getMetaValue,
  findElement,
  removeElement,
  insertAfter
} from '../helpers/helpers'

Dropzone.autoDiscover = false

// Connects to data-controller="dropzone"
export default class extends Controller {
  static targets = ['input']
  static values = {
    url: String
  }

  connect() {
    this.dropZone = createDropZone(this)
    this.hideFileInput()
    this.bindEvents()
    this.disable()
  }

  disable() {
    // Deshabilita el input de archivo
    this.inputTarget.disabled = true
    // Agrega una clase visual para indicar que está deshabilitado
    this.element.classList.add('dropzone-disabled')
    // Opción: Evitar que Dropzone procese clicks
    this.dropZone.options.clickable = false
  }

  enable() {
    // Habilita el input de archivo
    this.inputTarget.disabled = false
    // Remueve la clase visual
    this.element.classList.remove('dropzone-disabled')
    // Opción: Habilitar que Dropzone procese clicks
    this.dropZone.options.clickable = true
  }

  hideFileInput() {
    this.inputTarget.disabled = true
    this.inputTarget.style.display = "none"
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      setTimeout(() => { file.accepted && createDirectUploadController(this, file, this.urlValue).start() }, 500)
    })

    this.dropZone.on("removedfile", (file) => {
      file.controller && removeElement(file.controller.hiddenInput)
    })

    this.dropZone.on("canceled", (file) => {
      file.controller && file.controller.xhr.abort()
    })

    this.dropZone.on("processing", (file) => {})

    this.dropZone.on("queuecomplete", (file) => {
      this.dropZone.removeAllFiles()
    })
  }

  get headers() { return { "X-CSRF-Token": getMetaValue("csrf-token") } }

  get url() { return this.inputTarget.getAttribute("data-direct-upload-url") }

  get maxFiles() { return this.data.get("maxFiles") || 1 }

  get maxFileSize() { return this.data.get("maxFileSize") || 256 }

  get acceptedFiles() { return this.data.get("acceptedFiles") }

  get addRemoveLinks() { return this.data.get("addRemoveLinks") || true }

  get form() { return this.element.closest("form") }

  get submitButton() { return findElement(this.form, "input[type=submit], button[type=submit]") }
}

class DirectUploadController {
  constructor(source, file, url) {
    this.directUpload = createDirectUpload(file, source.url, this)
    this.source = source
    this.file = file
    this.url = url
  }

  start() {
    this.file.controller = this
    this.hiddenInput = this.createHiddenInput()
    this.directUpload.create((error, attributes) => {
      if (error) {
        removeElement(this.hiddenInput)
        this.emitDropzoneError(error)
      } else {
        this.hiddenInput.value = attributes.signed_id

        const tipoDocumento = document.getElementById("tipo_documento_select").value;
        const subTipoDocumento = document.getElementById("sub_tipo_documento_select").value;
        const fecha = document.getElementById("archivo_fecha").value;
        const hora = document.getElementById("archivo_hora").value;

        post(this.url, {
          responseKind: 'turbo-stream',
          body: {
            archivos: [attributes.signed_id],
            tipo_documento: tipoDocumento,
            sub_tipo_documento: subTipoDocumento,
            fecha: fecha,
            hora: hora
          }
        })
        this.emitDropzoneSuccess()
      }
    })
  }

  // Private
  createHiddenInput() {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.source.inputTarget.name
    insertAfter(input, this.source.inputTarget)
    return input
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr)
    this.emitDropzoneUploading()
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr
    this.xhr.upload.addEventListener("progress", event => this.uploadRequestDidProgress(event))
  }

  uploadRequestDidProgress(event) {
    const element = this.source.element
    const progress = event.loaded / event.total * 100
    findElement(this.file.previewTemplate, ".dz-upload").style.width = `${progress}%`
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING
    this.source.dropZone.emit("processing", this.file)
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR
    this.source.dropZone.emit("error", this.file, error)
    this.source.dropZone.emit("complete", this.file)
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.source.dropZone.emit("success", this.file)
    this.source.dropZone.emit("complete", this.file)
  }
}

function createDirectUploadController(source, file, url) {
  return new DirectUploadController(source, file, url)
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller)
}

function createDropZone(controller) {
  return new Dropzone(controller.element, {
    url: controller.url,
    headers: controller.headers,
    maxFilesize: controller.maxFileSize,
    acceptedFiles: 'image/png,image/jpg,image/jpeg,application/pdf',
    addRemoveLinks: false,
    uploadMultiple: true,
    autoQueue: false
  })
}
